import { toast } from 'sonner';

import { useGenerateAttendanceCSVMutation } from '~entities/classes/api/generate-attendance-csv';
import { useGenerateAttendancePDFMutation } from '~entities/classes/api/generate-attendance-pdf';

interface UseAttendanceExportParams {
  entityId: string;
  entityTitle?: string;
}

export type ExportFormat = 'pdf' | 'csv';

export const useAttendanceExport = ({
  entityId,
  entityTitle,
}: UseAttendanceExportParams) => {
  const generateAttendancePDFMutation = useGenerateAttendancePDFMutation();
  const generateAttendanceCSVMutation = useGenerateAttendanceCSVMutation();

  const exportAttendanceSheet = async (
    showBalance: boolean,
    format: ExportFormat = 'pdf',
  ) => {
    try {
      const mutationFn =
        format === 'csv'
          ? generateAttendanceCSVMutation.mutateAsync
          : generateAttendancePDFMutation.mutateAsync;

      const response = await mutationFn({
        classId: entityId,
        showBalance,
      });
      const fileExtension = format;

      const url = window.URL.createObjectURL(response);

      // Create an anchor element and set properties for download
      const link = document.createElement('a');
      link.href = url;
      link.download = `${entityTitle ?? 'attendance'}-sheet.${fileExtension}`;
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(
        `Attendance sheet downloaded successfully as ${format.toUpperCase()}`,
      );
      return true;
    } catch (error: unknown) {
      console.error(error);

      // Extract error message
      let errorMessage = 'Failed to generate attendance sheet';

      if (error && typeof error === 'object' && 'message' in error) {
        const errorObj = error as { message: string };
        errorMessage = errorObj.message;
      }

      toast.error(errorMessage);
      return false;
    }
  };

  return {
    exportAttendanceSheet,
    isExporting:
      generateAttendancePDFMutation.isPending ||
      generateAttendanceCSVMutation.isPending,
  };
};
